import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Boxes, HardHat, Sparkles, MapPin, UserCheck, Wrench } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './Card';
import Button from './Button';
import { servicesData, serviceCategories } from '../data/services';
import { motion } from 'framer-motion';

// Icon mapping
const iconMap = {
  Building2,
  Users,
  Boxes,
  HardHat,
  Sparkles,
  MapPin,
  UserCheck,
  Wrench
};

const ServicesGrid = ({ limit, category = 'all' }) => {
  const [activeCategory, setActiveCategory] = useState(category);

  const filteredServices = activeCategory === 'all'
    ? servicesData
    : servicesData.filter(service => service.category === activeCategory);

  const displayedServices = limit ? filteredServices.slice(0, limit) : filteredServices;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div>
      {/* Section Title - only show on homepage with limit */}
      {limit && (
        <div className="text-center mb-12">
          <h2 className="section-title">
            Unsere Leistungen
          </h2>
          <p className="section-subtitle">
            Umfassende Personal- und Logistiklösungen für alle Branchen.
            Von Gebäudemanagement bis Eventpersonal – alles aus einer Hand.
          </p>
        </div>
      )}

      {/* Category Filter */}
      {!limit && (
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {serviceCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-lg font-medium transition-all duration-200 ${
                activeCategory === cat.id
                  ? 'bg-primary-text-dark text-white shadow-md'
                  : 'bg-primary-gray-light text-primary-text-muted hover:bg-primary-gray-border'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Services Grid */}
      <motion.div
        key={activeCategory}
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {displayedServices.length > 0 ? (
          displayedServices.map((service) => {
          const IconComponent = iconMap[service.icon] || Users;

          return (
            <motion.div key={service.id} variants={item}>
              <Card className="h-full flex flex-col relative overflow-hidden group backdrop-blur-md bg-white/80 border-white/90">
                {/* Background Image with Overlay */}
                {service.image && (
                  <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-105">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-white/65 via-white/55 to-white/45"></div>
                  </div>
                )}

                <CardHeader className="relative z-10 backdrop-blur-sm bg-white/40 rounded-t-xl p-6">
                  <div className="w-14 h-14 rounded-xl bg-primary-blue-light/95 backdrop-blur-md flex items-center justify-center mb-4 shadow-lg border border-white/60">
                    <IconComponent className="w-7 h-7 text-primary-blue-mid" />
                  </div>
                  <CardTitle className="text-shadow">{service.title}</CardTitle>
                  <CardDescription className="text-shadow">{service.shortDescription}</CardDescription>
                </CardHeader>

                <CardContent className="flex-grow relative z-10 backdrop-blur-sm bg-white/30 p-6">
                  <p className="text-sm text-gray-700 leading-relaxed mb-4 font-medium">
                    {service.description}
                  </p>

                  {/* Key Features */}
                  {service.features && service.features.length > 0 && (
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-2 flex-shrink-0 shadow-sm"></span>
                          <span className="text-sm text-gray-700 font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>

                <CardFooter className="relative z-10 backdrop-blur-sm bg-white/30 p-6 rounded-b-xl">
                  <Link to={`/leistungen/${service.slug}`} className="w-full">
                    <Button
                      variant="ghost"
                      className="w-full justify-between group"
                    >
                      <span>Mehr erfahren</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </motion.div>
          );
        })
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-lg text-primary-text-muted">
              Keine Leistungen in dieser Kategorie gefunden.
            </p>
          </div>
        )}
      </motion.div>

      {/* CTA Section */}
      {limit && displayedServices.length >= limit && (
        <div className="text-center mt-12">
          <Link to="/leistungen">
            <Button variant="primary" size="lg" icon={ArrowRight} iconPosition="right">
              Alle Leistungen anzeigen
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ServicesGrid;
